import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { COLORS } from "colors/color";
import useSignalingStore from "../../views/signalingStore";
import ExitToAppRoundedIcon from "@mui/icons-material/ExitToAppRounded";
import SettingsIcon from "@mui/icons-material/Settings";
import HelpIcon from "@mui/icons-material/Help";
import myLogo from "../../assets/img/logo_.svg";
import logo from "../../assets/img/new_logo.svg";
import closet_icon from "../../assets/img/closet_icon.svg";
import closet_collapse_icon from "../../assets/img/closet_collapse_icon.svg";
import mySmallLogo from "../../assets/img/logo_small.svg";
import small_logo from "../../assets/img/small_logo.svg";
import settings_icon from "../../assets/img/settings_icon.svg";
import help_icon from "../../assets/img/help_icon.svg";
import logout_icon from "../../assets/img/logout_icon2.svg";
import x_icon from "../../assets/img/close_icon.svg";
import {
  Avatar,
  Badge,
  Box,
  Divider,
  Grid,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import atob from "atob";
import { useTranslation } from "react-i18next";
import colors from "colors/color";
import useFetchRecruiter from "hooks/useFetchRecruiter";

// Add focus mode overlay style
const FocusModeOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  pointer-events: none;
`;

const SidebarContainer = styled.div`
  width: ${({ $isSidebarCollapsed }) =>
    $isSidebarCollapsed ? "80px" : "270px"};
  background: #ffffff;
  height: 100%;
  position: fixed;
  top: 0px;
  left: 0px;
  display: flex;
  flex-direction: column;
  z-index: 100;
  border-radius: 0px;
  transition: width 0.3s ease, left 0.3s ease;
  overflow: hidden;
  font-family: Poppins;
  border-right: 1px solid #d9d9d9;

  @media (max-width: 768px) {
    width: ${({ $isMobile, $isSidebarCollapsed }) =>
      $isMobile ? ($isSidebarCollapsed ? "80px" : "250px") : "0"};
    left: ${({ $isMobile }) => ($isMobile ? "0" : "-250px")};
    position: fixed;
    z-index: 1036;
  }
`;

const Logo = styled.div`
  padding: 10px;
  text-align: center;
  font-size: 24px;
  font-weight: bold;
  color: #4a4aff;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    width: ${({ $isSidebarCollapsed }) =>
      $isSidebarCollapsed ? "50px" : "80%"};
    transition: width 0.6s ease;
  }

  @media (max-width: 768px) {
    img {
      width: ${({ $isSidebarCollapsed }) =>
        $isSidebarCollapsed ? "20px" : "80%"};
    }
  }
`;

const NavContainer = styled.nav`
  padding: 10px 0;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-grow: 0;
`;

const NavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  justify-content: ${({ $isSidebarCollapsed }) =>
    $isSidebarCollapsed ? "center" : "flex-start"};
  width: ${({ $isSidebarCollapsed }) => ($isSidebarCollapsed ? "100%" : "80%")};
  padding: 10px
    ${({ $isSidebarCollapsed }) => ($isSidebarCollapsed ? "0" : "20px")};
  color: #00000080;
  text-decoration: none;
  transition: all 0.3s ease;
  border-radius: 8px;
  margin-bottom: 5px;
  gap: ${({ $isSidebarCollapsed }) => ($isSidebarCollapsed ? "0" : "10px")};
  font-size: 14px;
  font-family: Poppins;
  font-weight: 600px;

  img {
    filter: brightness(0) saturate(100%) invert(0%) sepia(0%) saturate(0%)
      hue-rotate(0deg) brightness(50%) contrast(50%);
  }

  &:hover {
    background-color: #f5f5ff;
    color: ${colors.primary};
    img {
      filter: brightness(0) saturate(100%) invert(13%) sepia(100%)
        saturate(2000%) hue-rotate(240deg) brightness(90%) contrast(95%);
    }
  }

  &.active {
    background-color: #d9d9d91a;
    color: ${colors.primary};
    font-weight: 600;
    img {
      filter: brightness(0) saturate(100%) invert(13%) sepia(100%)
        saturate(2000%) hue-rotate(240deg) brightness(90%) contrast(95%);
    }
  }

  svg {
    font-size: 15px;
    fill: #888;
    transition: fill 0.3s ease;
  }

  p {
    font-weight: 400;
    padding-left: 16px;
    margin: 0;
    font-size: 12px;
    display: ${({ $isSidebarCollapsed }) =>
      $isSidebarCollapsed ? "none" : "block"};
  }

  @media (max-width: 768px) {
    justify-content: ${({ $isSidebarCollapsed }) =>
      $isSidebarCollapsed ? "center" : "flex-start"};
    width: ${({ $isSidebarCollapsed }) =>
      $isSidebarCollapsed ? "100%" : "80%"};
    padding: ${({ $isSidebarCollapsed }) =>
      $isSidebarCollapsed ? "12px 0" : "12px 20px"};
    gap: ${({ $isSidebarCollapsed }) => ($isSidebarCollapsed ? "0" : "10px")};

    p {
      display: ${({ $isSidebarCollapsed }) =>
        $isSidebarCollapsed ? "none" : "block"};
      padding-left: ${({ $isSidebarCollapsed }) =>
        $isSidebarCollapsed ? "0" : "16px"};
    }
  }
`;

const SettingsItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ $isSidebarCollapsed }) =>
    $isSidebarCollapsed ? "center" : "flex-start"};
  width: ${({ $isSidebarCollapsed }) => ($isSidebarCollapsed ? "100%" : "80%")};
  padding: 10px
    ${({ $isSidebarCollapsed }) => ($isSidebarCollapsed ? "0" : "20px")};
  color: #4d4d4d;
  text-decoration: none;
  transition: all 0.3s ease;
  border-radius: 8px;
  margin-bottom: 5px;
  gap: ${({ $isSidebarCollapsed }) => ($isSidebarCollapsed ? "0" : "10px")};
  font-size: 12px;
  font-family: Poppins;
  font-weight: 600px;
  cursor: pointer;
  &:hover {
    background-color: #f5f5ff;
  }

  &.active {
    background-color: #d9d9d91a;
    color: ${colors.primary};
    font-weight: 600;
    svg {
      fill: #020060;
    }
  }

  svg {
    font-size: 15px;
    fill: #888;
    transition: fill 0.3s ease;
  }

  p {
    font-weight: 400;
    margin: 0;
    font-size: 12px;
    display: ${({ $isSidebarCollapsed }) =>
      $isSidebarCollapsed ? "none" : "block"};
  }

  @media (max-width: 768px) {
    justify-content: ${({ $isSidebarCollapsed }) =>
      $isSidebarCollapsed ? "center" : "flex-start"};
    width: ${({ $isSidebarCollapsed }) =>
      $isSidebarCollapsed ? "100%" : "80%"};
    padding: ${({ $isSidebarCollapsed }) =>
      $isSidebarCollapsed ? "12px 0" : "12px 20px"};
    gap: ${({ $isSidebarCollapsed }) => ($isSidebarCollapsed ? "0" : "10px")};

    p {
      display: ${({ $isSidebarCollapsed }) =>
        $isSidebarCollapsed ? "none" : "block"};
    }
  }
`;

const NotificationBadge = styled.span`
  background-color: #ff3b30;
  color: ${COLORS.white};
  border-radius: 10px;
  padding: 2px 8px;
  font-size: 10px;
  margin-left: auto;
`;

const NotificationText = styled.span`
  margin-left: 4px;
  margin-top: 1px;
  margin-bottom: 1px;
  font-size: 10px;
  color: ${COLORS.white};
`;

const LogoutButton = styled.button`
  width: ${({ $isSidebarCollapsed }) => ($isSidebarCollapsed ? "100%" : "60%")};
  margin: 10px auto;
  padding: 10px;
  background-color: #f0f0ff;
  color: #020060;
  text-decoration: none;
  border: none;
  border-radius: 8px;
  text-align: center;
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ $isSidebarCollapsed }) => ($isSidebarCollapsed ? "0" : "10px")};
  font-size: 16px;

  svg {
    font-size: 20px;
    fill: #020060;
  }

  span {
    display: ${({ $isSidebarCollapsed }) =>
      $isSidebarCollapsed ? "none" : "block"};
  }

  @media (max-width: 768px) {
    width: 60%;
    gap: 10px;

    span {
      display: block;
    }
  }
`;
const CompanyLogo = styled(Box)(({ theme, logo }) => ({
  // minWidth: "44px",
  // minHeight: "44px",
  // maxWidth: "44px",
  // maxHeight: "44px",
  background:
    "linear-gradient(135deg, rgba(0, 23, 231, 0.5) -54.69%, rgba(255, 204, 248, 0.5) 66.41%)",
  borderRadius: 50,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  fontSize: "0.75rem",
  textAlign: "center",
  overflow: "hidden",
}));
const SettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
  width: 100%;
  margin-bottom: 0 !important;
`;

// New component for mobile toggle button that appears outside sidebar
const MobileToggleButton = styled.div`
  display: none;
  position: fixed;
  top: 10px;
  left: 10px;
  width: 40px;
  height: 40px;
  background-color: #ffffff;
  border-radius: 50%;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1035;
  @media (max-width: 768px) {
    display: ${({ $isMobileView }) => ($isMobileView ? "none" : "flex")};
  }
`;

function Sidebar({
  routes,
  isSidebarCollapsed = false,
  isMobile = false,
  onClose,
  onToggleSidebar,
  setIsSidebarCollapsed,
  onOpen,
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const sidebarRef = useRef(null);
  const navigate = useNavigate();
  const [totalNotifications, setTotalNotifications] = useState(0);
  const [collapsed, setCollapsed] = useState(isSidebarCollapsed);
  const { formData, loading, error } = useFetchRecruiter();

  const {
    notificationCounts,
    getTotalNotifications,
    setInitialNotificationCounts,
    updateNotificationCount,
    addMessageNotification,
  } = useSignalingStore();
  const theme = useTheme();
  const apiUrl = process.env.REACT_APP_API_URL;
  const apiUrlsoket = process.env.REACT_APP_API_URL_SOKET;
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  
  // Use isSmallScreen for mobile detection in addition to the isMobile prop
  const isMobileView = isMobile || isSmallScreen;

  // Update internal collapsed state when prop changes
  useEffect(() => {
    setCollapsed(isSidebarCollapsed);
  }, [isSidebarCollapsed]);

  useEffect(() => {
    setTotalNotifications(getTotalNotifications());
  }, [notificationCounts, getTotalNotifications]);

  useEffect(() => {
    fetchUnreadMessageCounts();
    // Note: WebSocket connections are now managed by WebSocketManager component

    if (isSmallScreen && !isMobile) {
      setCollapsed(false);
      setIsSidebarCollapsed(false);
    }
  }, [isSmallScreen]);

  const handleToggleSidebar = () => {
    const newCollapsedState = !collapsed;
    setCollapsed(newCollapsedState);
    setIsSidebarCollapsed(newCollapsedState);

    // If parent provided onToggleSidebar, call it with new state
    if (onToggleSidebar) {
      onToggleSidebar(newCollapsedState);
    }
  };

  const fetchUnreadMessageCounts = async () => {
    try {
      const response = await fetch(
        `${apiUrl}messaging/api/unread-message-counts/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch unread message counts");
      }
      const data = await response.json();
      setInitialNotificationCounts(data);
    } catch (error) {
      console.error("Error fetching unread message counts:", error);
    }
  };

  // Note: WebSocket connections are now managed by WebSocketManager component
  // Removed duplicate WebSocket creation logic to prevent infinite connection loops

  const getUserIdFromAccessToken = () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      console.error("No access token found in local storage");
      return null;
    }

    try {
      const decodedToken = parseJwt(accessToken);
      return decodedToken.user_id;
    } catch (error) {
      console.error("Failed to decode access token", error);
      return null;
    }
  };

  function parseJwt(token) {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );

    return JSON.parse(jsonPayload);
  }

  const handleLogout = () => {
    localStorage.removeItem("accessToken");
    navigate("/login");
  };

  const handleSettings = () => {
    navigate("/admin/settings");
    if (isSmallScreen && isMobile) {
      onClose();
    }
  };

  const handleHelpCenter = () => {
    navigate("/admin/help");
    if (isSmallScreen && isMobile) {
      onClose();
    }
  };

  const getNotificationDisplay = () => {
    if (totalNotifications === 0) return null;

    if (!collapsed) {
      // When sidebar is expanded
      const notifText = totalNotifications > 9 ? "9+" : totalNotifications;
      // console.log('totalNotifications',totalNotifications)
      return (
        <NotificationBadge>
          <Box display="flex" alignItems="center">
            <Typography component="span" sx={{ fontSize: "10px" }}>
              {notifText}
            </Typography>
            {/* <Typography component="span" sx={{ fontSize: "10px" }}>
              {t("newMessages")} 
            </Typography> */}
          </Box>
        </NotificationBadge>
      );
    } else {
      // When sidebar is collapsed - just return the number for the badge
      return totalNotifications > 9 ? "9+" : totalNotifications;
    }
  };

  // Function to show mobile sidebar
  const handleShowMobileSidebar = () => {
    if (onToggleSidebar) {
      onToggleSidebar(false);
    }
  };

  const getTourClassName = (routeName) => {
    // Special handling for the "posts" route to match the tour steps
    if (routeName === "posts") {
      return "nav-item-posts";
    }
    // Special handling for the "Import Data" route
    if (routeName === "Import Data") {
      return "nav-item-import-data";
    }
    return `nav-item-${routeName.toLowerCase().replace(/\s+/g, "-")}`;
  };

  return (
    <>
      {/* Mobile toggle button outside sidebar */}
      <MobileToggleButton $isMobileView={isMobileView} onClick={onOpen}>
        <img
          src={small_logo}
          alt="Menu"
          style={{ width: "20px", height: "auto" }}
        />
      </MobileToggleButton>

      <SidebarContainer $isSidebarCollapsed={collapsed} $isMobile={isMobileView}>
        {isMobileView ? (
          <Box
            sx={{
              paddingTop: 2,
              paddingRight: 2,
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <img
              src={x_icon}
              onClick={onClose}
              alt="close"
              style={{ width: "14px", height: "auto", cursor: "pointer" }}
            />
          </Box>
        ) : null}

        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{ padding: collapsed && isSmallScreen ? 1 : 2 }}
        >
          <Grid item>
            <Logo
              $isSidebarCollapsed={collapsed}
              onClick={() => navigate("/")}
              style={{ cursor: "pointer" }}
            >
              <img
                src={collapsed ? small_logo : logo}
                alt="TalentyHire Logo"
                style={{
                  objectFit: "contain",
                  width: collapsed ? "20px" : "124px",
                  height: "auto",
                }}
              />
            </Logo>
          </Grid>

          {isMobileView ? null : (
            <Grid
              item
              sx={{ paddingRight: collapsed && isSmallScreen ? 0 : 2 }}
            >
              <img
                src={collapsed ? closet_collapse_icon : closet_icon}
                alt="Toggle sidebar"
                style={{
                  width: "18px",
                  height: "18px",
                  cursor: "pointer",
                  transition: "transform 0.3s ease",
                  marginLeft: collapsed ? (isSmallScreen ? 6 : 12) : 0,
                  marginTop: collapsed ? (isSmallScreen ? 6 : 12) : 0,
                }}
                onClick={handleToggleSidebar}
              />
            </Grid>
          )}
        </Grid>
        {/* <Box sx={{ display: "flex", justifyContent: "center", minHeight: 40, minWidth:40 }}>
           
          <Avatar
                            sx={{
                              border: "1px solid #0017E7",
                              width: isSmallScreen ? 12 : 48,
                              height: isSmallScreen ? 12 : 48,
                            }}
                          >
                            {formData?.created_by?.company_logo ? (
                              <img
                                src={formData?.created_by?.company_logo}
                                alt="User"
                                style={{ width: "100%", height: "100%" }}
                              />
                            ) : (
                              formData?.created_by?.first_name?.charAt(0)
                            )}
                          </Avatar>
        </Box> */}

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          {collapsed ? (
            <Divider
              orientation="horizontal"
              sx={{
                borderTopWidth: 1,
                width: "50%",
                display: "flex",
                alignItems: "center",
              }}
            />
          ) : null}
        </Box>
        <Typography
          sx={{
            fontFamily: "Poppins",
            fontWeight: 500,
            fontSize: "14px",
            color: "#0000004D",
            paddingLeft: collapsed ? 1 : 3.5,
            paddingTop: 2,
            paddingBottom: 1,
            display: collapsed ? "none" : "block",
          }}
        >
          {t("main_menu")}
        </Typography>

        <NavContainer ref={sidebarRef}>
{routes.filter(route => !route.invisible).map((route, index) => (            <NavItem
              key={index}
              to={route.layout + route.path}
              $isSidebarCollapsed={collapsed}
              className={({ isActive }) => {
                const activeClass = isActive ? "active" : "";
                const tourClass = getTourClassName(route.name);
                return `${activeClass} ${tourClass}`.trim();
              }}
              onClick={isMobileView ? onClose : undefined}
              data-tour={
                route.name === "Import Data" ? "import-data" : undefined
              }
            >
              {typeof route.icon === "string" &&
              route.icon.startsWith("fas fa-") ? (
                <i className={route.icon} />
              ) : (
                <img
                  src={route.icon}
                  alt={route.name}
                  style={{ width: "15px", height: "15px" }}
                />
              )}
              <p>{t(route.name.startsWith('superAdmin') ? route.name : `routes.${route.name.toLowerCase().replace(" ", "_")}`)}</p>
              {route.name === "Chat" &&
                totalNotifications > 0 &&
                (collapsed ? (
                  <Badge
                    badgeContent={getNotificationDisplay()}
                    color="error"
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    sx={{
                      "& .MuiBadge-badge": {
                        fontSize: "12px",
                        minWidth: "20px",
                        height: "20px",
                        padding: "0 4px",
                      },
                    }}
                  />
                ) : (
                  getNotificationDisplay()
                ))}
            </NavItem>
          ))}
        </NavContainer>
        {/* {collapsed ? null : (
          <Typography
            sx={{
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: "14px",
              color: "#0000004D",
              paddingLeft: collapsed ? 1 : 3.5,
              marginBottom: 1,
            }}
          >
            Settings and more
          </Typography>
        )} */}
        {collapsed ? (
          <Box sx={{ width: "100%", marginTop: "0px" }}>
            <Divider
              orientation="horizontal"
              sx={{
                borderTopWidth: 1,
                width: "50%",
                display: "flex",
                alignItems: "center",
                margin: "0px auto",
              }}
            />
          </Box>
        ) : null}

        <Box
          sx={{
            width: "100%",
          }}
        >
          <SettingsContainer>
            {/* Settings button */}
            {/* <SettingsItem
              $isSidebarCollapsed={collapsed}
              onClick={handleSettings}
            >
              <img 
                src={settings_icon} 
                alt="Settings"
                style={{ 
                  width: "18px", 
                  height: "18px" 
                }}
              />
              <p>{t("settings")}</p>
            </SettingsItem>

            {/* Help Center button */}
            {/* <SettingsItem
              $isSidebarCollapsed={collapsed}
              onClick={handleHelpCenter}
            >
              <img 
                src={help_icon} 
                alt="Help" 
                style={{ 
                  width: "18px", 
                  height: "18px" 
                }} 
              />
              <p>{t("help_center")}</p>
            </SettingsItem>  */}

            {/* Logout button */}
            <SettingsItem
              $isSidebarCollapsed={collapsed}
              onClick={handleLogout}
              sx={{ color: "#FF3B30" }}
            >
              <img
                src={logout_icon}
                alt="Logout"
                style={{
                  width: "18px",
                  height: "18px",
                }}
              />
              <p style={{ color: "#FF3B30" }}>{t("logout")}</p>
            </SettingsItem>
          </SettingsContainer>
        </Box>
      </SidebarContainer>
    </>
  );
}

export default Sidebar;
